import { useCallback } from 'react';
import { DatabaseObjectType } from '../models/databaseObjectTypes';
import type { ExplorerFetchOrigin } from '../services/explorerQueryUtils';
import { useDatabaseQueries } from './useDatabaseQueries';
import { useConnectionStore } from '../../connections/store/connectionStore';

/**
 * Hook for fetching database data with engine-aware queries
 *
 * Looks up the connection to determine the database engine and passes it
 * to the underlying query functions for engine-specific SQL queries.
 */
export function useDatabaseFetch() {
  const { fetchDatabases, fetchObjects, invalidateDatabases, invalidateObjects } =
    useDatabaseQueries();

  // Get all connections to look up engine by connectionId
  // Check both saved connections and active connections (for browser mode SQLite)
  const connections = useConnectionStore((state) => state.connections);
  const activeConnections = useConnectionStore((state) => state.activeConnections);

  const getEngine = useCallback(
    (connectionId: string) => {
      // First check saved connections
      const savedConnection = connections.find((c) => c.id === connectionId);
      if (savedConnection?.engine) {
        return savedConnection.engine;
      }
      // Then check active connections (for browser mode SQLite connections)
      const activeConnection = activeConnections.find((c) => c.id === connectionId);
      if (activeConnection?.connection.engine) {
        return activeConnection.connection.engine;
      }
      return 'mssql';
    },
    [connections, activeConnections]
  );

  const fetchDatabasesForConnection = useCallback(
    (connectionId: string) => {
      const engine = getEngine(connectionId);
      return fetchDatabases(connectionId, engine);
    },
    [fetchDatabases, getEngine],
  );

  const fetchObjectsForFolder = useCallback(
    (
      objectType: DatabaseObjectType,
      connectionId: string,
      database: string,
      origin?: ExplorerFetchOrigin,
      /**
       * "The connect this fetch belongs to is still live." Only the
       * preload-bypass branch reads it — everything else goes through
       * `fetchQuery`, which `cancelQueries` can reach.
       */
      isCurrent?: () => boolean,
    ) => {
      const engine = getEngine(connectionId);
      return fetchObjects(objectType, connectionId, database, engine, origin, isCurrent);
    },
    [fetchObjects, getEngine],
  );

  return {
    fetchDatabasesForConnection,
    fetchObjectsForFolder,
    invalidateDatabases,
    invalidateObjects,
  };
}
